import { useState, useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AcademyProvider, useAcademy } from "./context/AcademyContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import Fees from "./pages/Fees";
import Payments from "./pages/Payments";
import Reports from "./pages/Reports";
import Batches from "./pages/Batches";
import { SkeletonBox } from "./components/Skeleton";
import Users from "./pages/Users";
import Attendance from "./pages/Attendance";
import Performance from "./pages/Performance";
import Expenses from "./pages/Expenses";
import IDCards from "./pages/IDCards";
import QRScanner from "./pages/QRScanner";
import Admissions from "./pages/Admissions";
import AdmissionForm from "./pages/AdmissionForm";
import StudentDashboard from "./pages/StudentDashboard";
import AcademySignup from "./pages/AcademySignup";
import AcademySettings from "./pages/AcademySettings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import "./App.css";

// Force redeploy triggered by model to ensure rollback to commit 7387429 is live.
const NAV_ICONS = {
  dashboard:   "▦",
  students:    "◉",
  admissions:  "✦",
  batches:     "▤",
  attendance:  "▣",
  performance: "◈",
  fees:        "◎",
  payments:    "⬡",
  expenses:    "◇",
  reports:     "▲",
  idcards:     "◻",
  qrscanner:   "⊞",
  users:       "◬",
  settings:    "⚙",
};

const BOTTOM_NAV_TABS = [
  { id: "dashboard",  label: "HOME",       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg> },
  { id: "students",   label: "STUDENTS",   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg> },
  { id: "fees",       label: "FEES",       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg> },
  { id: "payments",   label: "PAYMENTS",   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z"/></svg> },
  { id: "__more__",   label: "MORE",       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg> }
];

// ── Upgrade Contact Modal ────────────────────────────────────────────────
function UpgradeModal({ onClose }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: "32px 28px",
        maxWidth: 440,
        width: "100%",
        position: "relative",
      }}>
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 16,
            background: "none", border: "none", fontSize: 20,
            cursor: "pointer", color: "var(--text3)", lineHeight: 1,
          }}
        >✕</button>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🚀</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text1)", marginBottom: 4 }}>
            Upgrade Your Plan
          </div>
          <div style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.6 }}>
            Reach out to us and we'll get you upgraded within minutes.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <a
            href="https://wa.me/918956419453?text=Hi%20Kartik%2C%20I%20want%20to%20upgrade%20my%20Exponent%20academy%20plan."
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 18px",
              background: "rgba(37,211,102,0.1)",
              border: "1px solid rgba(37,211,102,0.3)",
              borderRadius: 12, textDecoration: "none",
            }}
          >
            <span style={{ fontSize: 28 }}>💬</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#25d366" }}>WhatsApp</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>+91 89564 19453 — fastest response</div>
            </div>
          </a>

          <a
            href="tel:+918956419453"
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 18px",
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.25)",
              borderRadius: 12, textDecoration: "none",
            }}
          >
            <span style={{ fontSize: 28 }}>📞</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--accent)" }}>Call Us</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>+91 89564 19453</div>
            </div>
          </a>

          <a
            href="mailto:aspirantth@gmail.com?subject=Upgrade%20Request%20%E2%80%94%20Exponent%20Plan&body=Hi%20Kartik%2C%0A%0AI%20would%20like%20to%20upgrade%20my%20academy%20plan.%0A%0AThank%20you."
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 18px",
              background: "rgba(168,85,247,0.08)",
              border: "1px solid rgba(168,85,247,0.25)",
              borderRadius: 12, textDecoration: "none",
            }}
          >
            <span style={{ fontSize: 28 }}>✉️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--purple, #a855f7)" }}>Email</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>aspirantth@gmail.com</div>
            </div>
          </a>
        </div>

        <div style={{ marginTop: 20, fontSize: 11, color: "var(--text3)", textAlign: "center" }}>
          We typically respond within a few hours — Mon to Sat.
        </div>
      </div>
    </div>
  );
}

// ── Trial expiry banner ────────────────────────────────────────────────────────
function TrialBanner({ academy, onUpgrade }) {
  if (!academy?.trial_ends_at || academy?.plan !== "trial") return null;
  const msLeft = new Date(academy.trial_ends_at) - Date.now();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  if (daysLeft > 7 || daysLeft < 0) return null;

  const urgent = daysLeft <= 2;
  return (
    <div style={{
      background: urgent ? "rgba(239,68,68,0.12)" : "rgba(251,191,36,0.10)",
      border: `1px solid ${urgent ? "rgba(239,68,68,0.35)" : "rgba(251,191,36,0.3)"}`,
      borderRadius: 10, padding: "10px 16px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      margin: "0 0 16px", flexWrap: "wrap", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{urgent ? "🚨" : "⏳"}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: urgent ? "var(--red)" : "var(--yellow)" }}>
          {daysLeft <= 0
            ? "Your free trial has expired!"
            : `Your free trial expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}!`}
        </span>
        <span style={{ fontSize: 12, color: "var(--text3)" }}>
          Ends on {new Date(academy.trial_ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
        </span>
      </div>
      <button
        className="btn btn-sm"
        style={{ background: urgent ? "rgba(239,68,68,0.2)" : "rgba(251,191,36,0.15)", color: urgent ? "var(--red)" : "var(--yellow)", border: "none" }}
        onClick={onUpgrade}
      >
        Contact Us to Upgrade →
      </button>
    </div>
  );
}

function Layout() {
  const { user, logout }     = useAuth();
  const { academy, loading } = useAcademy();
  const [page, setPage]               = useState("dashboard");
  const [pageState, setPageState]     = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme]             = useState(() => localStorage.getItem("theme") || "dark");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const mainRef = useRef(null);

  // Detect mobile (≤768px) — used to show bottom nav
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("light", theme === "light");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [page]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Handle Auth and Session
  if (!user) return <Login />;
  if (user.role === "student") return (
    <ErrorBoundary page="Student Dashboard">
      <StudentDashboard />
    </ErrorBoundary>
  );

  const nav = [
    { id: "dashboard",   label: "Dashboard",   group: "overview", show: true },
    { id: "students",    label: "Students",    group: "academic", show: true },
    { id: "admissions",  label: "Admissions",  group: "academic", show: !loading && (academy?.features?.admissions !== false) },
    { id: "batches",     label: "Batches",     group: "academic", show: true },
    { id: "attendance",  label: "Attendance",  group: "academic", show: !loading && (academy?.features?.attendance !== false) },
    { id: "performance", label: "Performance", group: "academic", show: !loading && (academy?.features?.tests !== false) },
    { id: "fees",        label: "Fee Records", group: "finance",  show: true },
    { id: "payments",    label: "Payments",    group: "finance",  show: true },
    { id: "expenses",    label: "Expenses",    group: "finance",  show: !loading && (academy?.features?.expenses !== false) },
    { id: "reports",     label: "Reports",     group: "finance",  show: !loading && (academy?.features?.reports !== false) },
    { id: "idcards",     label: "ID Cards",    group: "tools",    show: !loading && (academy?.features?.id_cards !== false) },
    { id: "qrscanner",   label: "QR Scanner",  group: "tools",    show: !loading && (academy?.features?.qr_scanner !== false) },
    ...(user.role === "super_admin" ? [
      { id: "users",    label: "Users",    group: "tools", show: true },
      { id: "settings", label: "Settings", group: "tools", show: true },
    ] : []),
  ].filter((n) => n.show);

  const pages = {
    dashboard:   Dashboard,
    students:    Students,
    admissions:  Admissions,
    batches:     Batches,
    attendance:  Attendance,
    performance: Performance,
    fees:        Fees,
    payments:    Payments,
    expenses:    Expenses,
    reports:     Reports,
    idcards:     IDCards,
    qrscanner:   QRScanner,
    users:       Users,
    settings:    AcademySettings,
  };
  const Page = pages[page] || Dashboard;
  const goTo = (id, state = null) => {
    setPage(id);
    setPageState(state);
    setSidebarOpen(false);
  };

  const groups = [
    { key: "overview", label: "Overview" },
    { key: "academic", label: "Academic" },
    { key: "finance",  label: "Finance" },
    { key: "tools",    label: "Tools" },
  ];

  const academyWords = (academy?.name || "Academy").split(" ");
  const brandTitle   = academyWords[0].toUpperCase();
  const brandSub     = academyWords.slice(1).join(" ") || "Portal";

  // Filter bottom nav tabs to only show ones the user has access to
  const visibleNavIds = new Set(nav.map(n => n.id));
  const bottomTabs = BOTTOM_NAV_TABS.filter(t =>
    t.id === "__more__" || visibleNavIds.has(t.id)
  );

  const handleBottomTab = (tabId) => {
    if (tabId === "__more__") {
      setSidebarOpen(true);
    } else {
      goTo(tabId);
    }
  };
      {/* Hamburger — only shown on desktop (hidden on mobile by mobile.css) */}
      {!isMobile && (
        <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu" style={{ display: 'block' }}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
      )}
  return (
    <div className="app-shell">
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}



      <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-brand">
          {academy?.logo_url ? (
            <img src={academy.logo_url} alt={academy.name}
              style={{ width: 36, height: 36, objectFit: "contain", borderRadius: 8 }} />
          ) : (
            <div style={{
              width: 36, height: 36, borderRadius: 8, background: "var(--blue-600)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 18, flexShrink: 0
            }}>
              {(academy?.name || "A")[0].toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="brand-title">{brandTitle}</div>
            <div className="brand-sub">{brandSub}</div>
          </div>
          <button className="theme-toggle" onClick={toggleTheme} title="Toggle theme">
            {theme === "dark" ? "☀" : "◑"}
          </button>
        </div>

        <nav className="sidebar-nav">
          {groups.map((group) => {
            const items = nav.filter((n) => n.group === group.key);
            if (!items.length) return null;
            return (
              <div key={group.key}>
                <div style={{
                  fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.12em", color: "var(--text3)",
                  padding: "12px 12px 4px", userSelect: "none",
                }}>
                  {group.label}
                </div>
                {items.map((n) => (
                  <button key={n.id} className={`nav-item ${page === n.id ? "active" : ""}`} onClick={() => goTo(n.id)}>
                    <span className="nav-icon" style={{ fontFamily: "monospace" }}>{NAV_ICONS[n.id] || "▸"}</span>
                    <span>{n.label}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-pill">
            <div className="user-avatar">{user.name?.[0]?.toUpperCase() || "U"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="user-name" style={{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{user.name}</div>
              <div className="user-role">{user.role === "super_admin" ? "Super Admin" : user.branch_name || "Manager"}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={logout}>← Logout</button>
        </div>
      </aside>

      <main className="main-content" ref={mainRef}>
        <TrialBanner academy={academy} onUpgrade={() => setShowUpgrade(true)} />
        {loading ? (
          <div style={{ animation: "fadeUp 0.3s ease both" }}>
            <div className="page-header">
              <div>
                <div className="skeleton-box" style={{ width: 200, height: 28, marginBottom: 8 }} />
                <div className="skeleton-box" style={{ width: 140, height: 16 }} />
              </div>
            </div>
            <div className="stat-grid">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="stat-card" style={{ border: "1px solid var(--border2)" }}>
                   <SkeletonBox width="60%" height="12px" marginBottom="12px" />
                   <SkeletonBox width="100%" height="32px" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <ErrorBoundary page={page} onNavigate={goTo}>
            <Page onNavigate={goTo} pageState={pageState} />
          </ErrorBoundary>
        )}
      </main>

      {/* ── Mobile Bottom Navigation Bar ───────────────────────────────────── */}
      <nav className="mobile-bottom-nav" style={{ display: isMobile ? 'flex' : 'none', justifyContent: 'space-around', alignItems: 'center', backgroundColor: '#1b2234', position: 'fixed', bottom: 0, left: 0, right: 0, padding: '10px 10px 24px 10px', zIndex: 1000, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {BOTTOM_NAV_TABS.map((tab) => {
          const isActive = page === tab.id;
          const isActuallyActive = isActive || (tab.id === '__more__' && sidebarOpen);
          
          if (isActuallyActive) {
            return (
              <button key={tab.id} onClick={() => handleBottomTab(tab.id)} style={{ background: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: 4 }}>
                <div style={{ backgroundColor: 'rgba(141, 156, 255, 0.1)', color: 'rgb(141, 156, 255)', width: 44, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {tab.icon}
                </div>
                <span style={{ fontSize: 9, fontWeight: 800, color: 'rgb(141, 156, 255)', letterSpacing: '0.05em' }}>{tab.label}</span>
              </button>
            )
          }

          return (
            <button
              key={tab.id}
              onClick={() => handleBottomTab(tab.id)}
              style={{ background: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: 4, color: '#7c8b9d' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 32 }}>
                {tab.icon}
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em' }}>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default function App() {
  if (window.location.pathname === "/apply")   return <AdmissionForm />;
  if (window.location.pathname === "/signup")  return <AcademySignup />;
  if (window.location.pathname === "/privacy") return <PrivacyPolicy />;
  if (window.location.pathname === "/terms")   return <TermsOfService />;
  if (window.location.pathname === "/contact") return <Contact />;
  return (
    <AcademyProvider>
      <AuthProvider>
        <Layout />
      </AuthProvider>
    </AcademyProvider>
  );
}
